// components/GuideGestionUtilisateurs.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Info, Users, Shield, User, Key, Crown,
  ChevronDown, ChevronUp, UserPlus, Monitor,
  AlertCircle, CheckCircle, Mail, Lock, Building2
} from 'lucide-react';

const COLOR = '#448696';
const COLOR_DARK = '#336b78';

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '15' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-3 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ color = '#448696', label, desc, exemple, conseil }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
        {exemple && (
          <p className="text-xs text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-500">Ex :</span> {exemple}
          </p>
        )}
        {conseil && (
          <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR_DARK }}>{conseil}</p>
        )}
      </div>
    </div>
  </div>
);

const CompareRow = ({ label, employe, utilisateur }) => (
  <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-gray-100 last:border-0">
    <p className="text-xs font-semibold text-gray-600">{label}</p>
    <p className="text-xs text-gray-500 leading-relaxed">{employe}</p>
    <p className="text-xs text-gray-500 leading-relaxed">{utilisateur}</p>
  </div>
);

const GuideGestionUtilisateurs = ({ onClose }) => {
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between" style={{ backgroundColor: COLOR }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <Users size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide des comptes utilisateurs</h2>
              <p className="text-xs text-white/75 mt-0.5">Rôles, droits d'accès et gestion des comptes</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              Ce module gère les comptes de connexion à l'application. Il est distinct de la gestion des employés. Comprendre la différence entre un employé et un utilisateur est essentiel pour une configuration correcte de votre organisation.
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          {/* Différence employé vs utilisateur */}
          <AccordionSection title="Employé vs Utilisateur — quelle différence ?" colorBar={COLOR} icon={Users} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 mb-2">
              <p className="text-xs text-blue-700 leading-relaxed">
                C'est la distinction fondamentale de l'application. Un employé et un utilisateur ne sont pas la même chose, même si un utilisateur est nécessairement un employé de l'organisation.
              </p>
            </div>

            {/* Tableau comparatif */}
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-3 gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide"></p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                    <User size={12} className="text-white" />
                  </div>
                  <p className="text-xs font-bold text-gray-700">Employé</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fed774' }}>
                    <Monitor size={12} className="text-gray-900" />
                  </div>
                  <p className="text-xs font-bold text-gray-700">Utilisateur</p>
                </div>
              </div>
              <div className="px-4">
                <CompareRow
                  label="Définition"
                  employe="Toute personne travaillant dans l'entreprise, qu'elle ait ou non accès à l'application."
                  utilisateur="Un employé disposant d'un compte de connexion à l'application HoraPilot."
                />
                <CompareRow
                  label="Accès à l'app"
                  employe="Aucun accès. Il pointe via le kiosque mais ne se connecte pas à l'interface de gestion."
                  utilisateur="Se connecte à l'application avec un identifiant et un mot de passe."
                />
                <CompareRow
                  label="Géré dans"
                  employe="Module Employés — liste, fiche, pointage, paie, documents."
                  utilisateur="Ce module — Paramètres > Comptes utilisateurs."
                />
                <CompareRow
                  label="Exemple"
                  employe="Un ouvrier, un technicien, un agent de service qui pointe chaque matin au kiosque."
                  utilisateur="Le responsable RH, le directeur, le gestionnaire de site qui consulte les rapports."
                />
              </div>
            </div>

            <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 mt-1">
              <p className="text-xs text-amber-700 leading-relaxed">
                <span className="font-semibold">À retenir :</span> vous pouvez avoir 200 employés et seulement 3 utilisateurs.
              </p>
            </div>
          </AccordionSection>

          {/* Les types de comptes */}
          <AccordionSection title="Les types de comptes utilisateurs" colorBar="#b08d1a" icon={Crown} defaultOpen={true}>

            {/* Compte Admin Principal */}
            <div className="p-4 rounded-xl border-2 bg-amber-50/30" style={{ borderColor: '#fed774' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fed774' }}>
                  <Crown size={15} className="text-gray-900" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Administrateur principal</p>
                  <p className="text-xs text-amber-700 font-medium">Créé automatiquement par DANICED GROUP</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">
                Ce compte est créé automatiquement par DANICED GROUP lors de la souscription à votre abonnement. Il s'agit du compte maître de votre organisation — il a accès à tous les modules et fonctionnalités sans restriction.
              </p>
              <div className="space-y-1.5 mt-3">
                <p className="text-xs font-semibold text-gray-600 mb-1">Ce que vous pouvez faire sur ce compte :</p>
                {[
                  { ok: true,  text: 'Modifier le nom, le prénom et le numéro de téléphone' },
                  { ok: true,  text: 'Modifier la photo de profil' },
                  { ok: false, text: 'Modifier l\'adresse email — contacter le support DANICED GROUP' },
                  { ok: false, text: 'Changer son rôle Administrateur — impossible par sécurité' },
                  { ok: false, text: 'Supprimer ce compte — impossible par sécurité' },
                  { ok: false, text: 'Modifier ses droits d\'accès — accès total permanent' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {item.ok
                      ? <CheckCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                      : <X size={13} className="flex-shrink-0 mt-0.5 text-red-400" />
                    }
                    <p className="text-xs text-gray-500">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 px-3 py-2 rounded-lg bg-white border border-amber-200">
                <p className="text-xs text-amber-700">
                  <span className="font-semibold">Si le responsable change :</span> modifiez simplement le nom et les coordonnées du compte pour les remplacer par ceux du nouveau responsable. Il n'est pas nécessaire de créer un nouveau compte.
                </p>
              </div>
            </div>

            {/* Comptes utilisateurs standards */}
            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30 mt-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                  <User size={15} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Utilisateurs standards</p>
                  <p className="text-xs text-gray-500 font-medium">Créés par l'administrateur principal</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">
                Ce sont les autres comptes que l'administrateur principal crée selon les besoins de l'organisation. Ils peuvent être modifiés, supprimés et leurs droits d'accès peuvent être configurés librement.
              </p>
              <div className="space-y-1.5">
                {[
                  { ok: true, text: 'Modification complète : nom, email, téléphone, photo' },
                  { ok: true, text: 'Suppression à tout moment' },
                  { ok: true, text: 'Configuration des droits d\'accès par module' },
                  { ok: true, text: 'Réinitialisation du mot de passe' },
                  { ok: true, text: 'Activation / désactivation du compte' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                    <p className="text-xs text-gray-500">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </AccordionSection>

          {/* Le quota */}
          <AccordionSection title="Le quota d'utilisateurs" colorBar="#ef4444" icon={Shield} defaultOpen={false}>
            <GuideCard
              color="#ef4444"
              label="Pourquoi un quota ?"
              desc="Votre licence HoraPilot définit un nombre maximum de comptes utilisateurs autorisés. Ce quota ne s'applique qu'aux utilisateurs de l'application — pas aux employés."
              conseil="Si le bouton Nouvel utilisateur est grisé, vous avez atteint le quota de votre licence. Contactez DANICED GROUP pour augmenter ce quota."
            />
            <GuideCard
              color="#448696"
              label="Quota affiché dans les statistiques"
              desc="Le compteur en haut de la page affiche le nombre d'utilisateurs actuels, le maximum autorisé, et le nombre de créations encore disponibles."
              exemple="3 / 5 — 2 disponibles : vous avez 3 utilisateurs sur 5 autorisés, il vous reste 2 créations possibles."
            />
          </AccordionSection>

          {/* Cas d'usage kiosque */}
          <AccordionSection title="Cas d'usage recommandé — les comptes Kiosque" colorBar="#7c3aed" icon={Monitor} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-purple-50 border border-purple-100 mb-2">
              <p className="text-xs text-purple-700 leading-relaxed">
                Pour les entreprises utilisant HoraPilot principalement pour la gestion des pointages, voici l'organisation recommandée par DANICED GROUP.
              </p>
            </div>
            <GuideCard
              color="#7c3aed"
              label="Pourquoi créer des comptes Kiosque ?"
              desc="Si votre entreprise dispose de plusieurs sites ou points de pointage, créez un compte utilisateur dédié par site plutôt qu'un compte nominatif. Un compte Kiosque est associé à un poste physique, pas à une personne. Cela évite de devoir modifier le compte si le responsable du site change."
              exemple="Kiosque Site Brazzaville, Kiosque Site Pointe-Noire, Kiosque Entrepôt Nord."
              conseil="Nommez les comptes selon le site ou le poste physique, pas selon le nom de la personne qui les utilise."
            />
            <GuideCard
              color="#7c3aed"
              label="Droits à attribuer aux comptes Kiosque"
              desc="Un compte Kiosque ne doit avoir accès qu'à la vue de pointage des employés. Dans les droits d'accès, activez uniquement le module Pointage avec la vue Employé. Ce compte ne verra que l'interface de pointage au moment de la connexion — aucun autre module ne sera accessible."
              conseil="Un compte Kiosque bien configuré est sécurisé : même si quelqu'un accède au poste, il ne peut consulter ni les données RH, ni la comptabilité, ni les rapports."
            />
            <GuideCard
              color="#448696"
              label="Fonctionnement au quotidien"
              desc="Le compte Kiosque reste connecté en permanence sur le poste de pointage. Les employés ne se connectent pas eux-mêmes — ils scannent leur badge ou saisissent leur numéro. L'administrateur, lui, se connecte depuis son propre compte pour consulter les rapports et gérer les pointages."
            />
          </AccordionSection>

          {/* Droits d'accès */}
          <AccordionSection title="Configurer les droits d'accès" colorBar="#b08d1a" icon={Key} defaultOpen={false}>
            <GuideCard
              color="#b08d1a"
              label="Principe des droits d'accès"
              desc="Chaque utilisateur standard peut se voir accorder ou refuser l'accès à chaque module de l'application. Un module non accordé n'apparaît tout simplement pas dans l'interface de l'utilisateur quand il se connecte."
              conseil="Appliquez le principe du moindre privilège : accordez uniquement les accès nécessaires à chaque rôle dans votre organisation."
            />
            <GuideCard
              color="#b08d1a"
              label="Comment modifier les droits"
              desc="Cliquez sur le bouton Droits d'accès sur la ligne de l'utilisateur concerné. Un panneau de configuration s'ouvre avec la liste de tous les modules disponibles. Cochez ou décochez selon les besoins, puis sauvegardez."
              conseil="Les modifications de droits prennent effet immédiatement — si l'utilisateur est connecté, il devra recharger la page."
            />
            <GuideCard
              color="#ef4444"
              label="Droits de l'administrateur principal"
              desc="Le compte administrateur principal a accès à tous les modules sans restriction. Il n'est pas possible de modifier ses droits d'accès — ce compte dispose toujours d'un accès complet à l'ensemble de l'application."
            />
          </AccordionSection>

          {/* Statuts */}
          <AccordionSection title="Statuts des comptes" colorBar="#22c55e" icon={CheckCircle} defaultOpen={false}>
            <GuideCard
              color="#22c55e"
              label="Compte Actif"
              desc="L'utilisateur peut se connecter normalement à l'application. Son compte est opérationnel."
            />
            <GuideCard
              color="#ef4444"
              label="Compte Inactif"
              desc="L'utilisateur ne peut plus se connecter à l'application. Ses données sont conservées mais l'accès est bloqué. Utile pour suspendre temporairement un accès sans supprimer le compte."
              conseil="Préférez désactiver un compte plutôt que de le supprimer si vous pensez devoir le réactiver plus tard."
            />
          </AccordionSection>

          {/* Bonne pratique */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-2">Organisation recommandée</p>
            <div className="space-y-1.5">
              {[
                "Conservez le compte administrateur principal pour la gestion globale de l'application.",
                "Créez des comptes Kiosque par site physique pour les postes de pointage — nommez-les par site, pas par personne.",
                "Créez des comptes nominatifs uniquement pour les responsables ayant besoin d'accéder aux rapports et à la gestion RH.",
                "Limitez les droits de chaque compte au strict nécessaire pour son usage quotidien.",
                "Pour modifier l'email du compte administrateur, contactez le support DANICED GROUP.",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: COLOR }} />
                  <p className="text-xs text-gray-500 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Support : <span className="font-semibold text-gray-600">contact@danicedgroup.com</span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuideGestionUtilisateurs;